import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import { categoryMeta, regionOptions, videos, yearOptions, type VideoCategory } from "@/mocks/videos";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 12;

const typeOptions: { value: string; label: string }[] = [
  { value: "全部", label: "全部类型" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
  { value: "variety", label: "综艺" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q")?.trim() ?? "";

  const [keyword, setKeyword] = useState(q);
  const [type, setType] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [page, setPage] = useState(1);

  usePageMeta({
    title: q ? `“${q}”的搜索结果 - 高清视频免费观看` : "搜索影片 - 高清视频免费观看",
    description: q
      ? `高清视频免费观看为你提供与“${q}”相关的电影、电视剧、综艺在线观看结果，快速找到想看的高清内容。`
      : "在高清视频免费观看搜索电影、电视剧、综艺，海量高清视频资源一键直达。",
    keywords: q ? `${q},${q}在线观看,搜索影片,高清视频免费观看` : "搜索影片,高清视频免费观看,在线视频搜索",
  });

  const results = useMemo(() => {
    const key = q.toLowerCase();
    if (!key) return [];
    const isType = (v: (typeof videos)[number]) =>
      type === "全部" ? true : v.category === (type as VideoCategory);
    return videos
      .filter((v) => {
        const hit = v.title.toLowerCase().includes(key) || v.description.toLowerCase().includes(key);
        if (!hit) return false;
        if (!isType(v)) return false;
        if (region !== "全部" && v.region !== region) return false;
        if (year !== "全部" && String(v.year) !== year) return false;
        return true;
      })
      .sort((a, b) => b.rating - a.rating);
  }, [q, type, region, year]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = results.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    const next = keyword.trim();
    setPage(1);
    setSearchParams(next ? { q: next } : {});
  };

  const resetFilters = () => {
    setType("全部");
    setRegion("全部");
    setYear("全部");
    setPage(1);
  };

  const changeFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const selectClass =
    "px-3 py-2 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer";

  return (
    <div className="bg-background-100">
      <div className="bg-background-50 border-b border-background-200">
        <div className="max-w-[1360px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 mt-4">搜索影片</h1>

          <form onSubmit={onSearch} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入片名或关键词..."
                className="w-full pl-10 pr-3 py-3 rounded-md bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:border-primary-400 outline-none"
              />
            </div>
            <button
              type="submit"
              className="px-7 py-3 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-[1360px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
        {q ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm md:text-base text-foreground-600">
                搜索关键词：
                <span className="font-heading font-bold text-primary-600 text-lg mx-1">“{q}”</span>
                共找到 <span className="font-semibold text-foreground-900">{results.length}</span> 个结果
              </p>
            </div>

            <div className="mt-5 rounded-lg bg-background-50 border border-background-200 p-4 md:p-5 flex flex-wrap items-center gap-3 md:gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">类型</span>
                <select value={type} onChange={(e) => changeFilter(setType)(e.target.value)} className={selectClass}>
                  {typeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">地区</span>
                <select value={region} onChange={(e) => changeFilter(setRegion)(e.target.value)} className={selectClass}>
                  {regionOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === "全部" ? "全部地区" : opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">年份</span>
                <select value={year} onChange={(e) => changeFilter(setYear)(e.target.value)} className={selectClass}>
                  {yearOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === "全部" ? "全部年份" : `${opt}年`}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={resetFilters}
                className="ml-auto inline-flex items-center gap-1 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置
              </button>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {pageItems.map((video) => (
                    <VideoCard key={video.id} video={video} showDesc />
                  ))}
                </div>
                <div className="mt-8 md:mt-10">
                  <Pagination currentPage={current} totalPages={totalPages} onChange={setPage} />
                </div>
              </>
            ) : (
              <div className="mt-6 rounded-lg bg-background-50 border border-background-200 py-16 text-center">
                <span className="w-16 h-16 mx-auto rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-3xl"></i>
                </span>
                <h3 className="font-heading font-bold text-lg text-foreground-900 mt-4">没有找到与“{q}”相关的内容</h3>
                <p className="text-sm text-foreground-500 mt-2">试试更换关键词，或调整类型、地区、年份筛选条件。</p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-semibold hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-refresh-line"></i>
                    重置筛选
                  </button>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-home-4-line"></i>
                    返回首页
                  </Link>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg bg-background-50 border border-background-200 py-16 text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
              <i className="ri-search-line text-3xl"></i>
            </span>
            <h3 className="font-heading font-bold text-lg text-foreground-900 mt-4">输入关键词开始搜索</h3>
            <p className="text-sm text-foreground-500 mt-2">试试搜索“星际”“悬疑”“长安”等热门关键词。</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              {["星际", "长安", "悬疑", "美食", "音乐"].map((hot) => (
                <Link
                  key={hot}
                  to={`/search?q=${encodeURIComponent(hot)}`}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors"
                >
                  {hot}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Quick category shortcuts */}
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <span className="text-sm text-foreground-500">热门频道：</span>
          {(Object.keys(categoryMeta) as VideoCategory[]).map((key) => (
            <Link
              key={key}
              to={`/category/${categoryMeta[key].slug}`}
              className="px-3 py-1.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
            >
              {categoryMeta[key].label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}